import {
  createParseState,
  processChunk,
  flushBuffer,
  type ParseState,
  type ReasoningInterval,
} from "./openCodeParser";
import type { WebhookStreamEvent } from "./types";

export interface WebhookParseState {
  buffer: string;
  innerState: ParseState;
}

export function createWebhookParseState(): WebhookParseState {
  return {
    buffer: "",
    innerState: createParseState(),
  };
}

export interface StreamCallbacks {
  onContent: (markdown: string) => void;
  onThinking?: (text: string, intervals?: ReasoningInterval[]) => void;
  onEnd: () => void;
}

function handleWrapperObject(
  objStr: string,
  innerState: ParseState,
  callbacks: StreamCallbacks,
): void {
  let event: WebhookStreamEvent;
  try {
    event = JSON.parse(objStr) as WebhookStreamEvent;
  } catch {
    return;
  }

  if (event.type === "item") {
    const { content, thinking, thinkingIntervals } = processChunk(
      event.content,
      innerState,
    );
    if (content) callbacks.onContent(content);
    if (thinking) callbacks.onThinking?.(thinking, thinkingIntervals);
  } else if (event.type === "end") {
    const { content, thinking, thinkingIntervals } = flushBuffer(innerState);
    if (content) callbacks.onContent(content);
    if (thinking) callbacks.onThinking?.(thinking, thinkingIntervals);
    callbacks.onEnd();
  }
}

/**
 * Feed a decoded text chunk from the stream into the parser.
 * The outer format is NDJSON (newline-delimited JSON),
 * so we split by newlines and parse each line as a separate JSON object.
 */
export function processWebhookChunk(
  chunk: string,
  state: WebhookParseState,
  callbacks: StreamCallbacks,
): void {
  state.buffer += chunk;
  const lines = state.buffer.split("\n");

  // Keep the last incomplete line in the buffer
  state.buffer = lines.pop() ?? "";

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    handleWrapperObject(trimmed, state.innerState, callbacks);
  }
}
